/**
 * #776 — the restricted-link disclosure: before a visitor's first edit through a link that
 * NAMES them, they are told their email (and organization) will be recorded in the source
 * org's file history, visible to anyone who can view it — once per source org.
 *
 * The server is the gate (every share-view write answers 409 `attribution_disclosure_required`
 * until they accept; live editors open read-only). This module is the visitor's side of it:
 *
 *   - `shareWriteFetch` — a share-view write: on that 409 it asks, records the acceptance
 *     and retries once; on "Not now" it hands the caller the original refusal.
 *   - `AttributionEditBanner` — on a read-only editor, "Start editing" asks; on acceptance the
 *     chrome re-reads the view (`ATTRIBUTION_ACCEPTED_EVENT`)
 *     so the editor reconnects writable.
 *   - `AttributionDisclosureHost` — the one dialog, mounted once by the share-view chrome.
 *
 * Rendered outside ThemedArea (the share-view chrome), so the theme class is applied to the
 * dialog content — never the backdrop.
 */
import { useEffect, useState, useSyncExternalStore } from 'react'
import { ShieldAlert } from 'lucide-react'
import { currentSessionSignal, dropIfSessionEnded, getApiBase, sessionFetch } from '@papan/api-client'
import { useTranslation } from '@papan/i18n'

export interface AttributionDisclosureInfo {
  sourceOrgName: string | null
  email: string
  recordsOrg: boolean
}

interface Pending {
  sessionId: string
  disclosure: AttributionDisclosureInfo
  promise: Promise<boolean>
  resolve: (accepted: boolean) => void
  /** #1316: the session-end listeners this question registered — removed when it settles. */
  detach: Array<() => void>
}

let pending: Pending | null = null
const listeners = new Set<() => void>()
const emit = () => { for (const l of listeners) l() }

/** Ask the visitor (one dialog at a time — concurrent writes share the answer). Resolves
 *  true once the acceptance is RECORDED server-side, false on "Not now" — and false, the dialog
 *  closed, when `session` (the one the write started in) ends first (#1316): the old session's
 *  question is never left up for whoever is here next. */
export function requestAttributionDisclosure(sessionId: string, disclosure: AttributionDisclosureInfo, session?: AbortSignal): Promise<boolean> {
  if (session?.aborted) return Promise.resolve(false)
  if (!pending) {
    let resolve!: (accepted: boolean) => void
    const promise = new Promise<boolean>((r) => { resolve = r })
    pending = { sessionId, disclosure, promise, resolve, detach: [] }
    emit()
  }
  const p = pending
  if (session) {
    const onEnd = () => { if (pending === p) settle(false) }
    session.addEventListener('abort', onEnd, { once: true })
    p.detach.push(() => session.removeEventListener('abort', onEnd))
  }
  return p.promise
}

function settle(accepted: boolean) {
  const p = pending
  pending = null
  for (const off of p?.detach ?? []) off()
  emit()
  p?.resolve(accepted)
}

/** Window event fired once the server recorded an acceptance — the share-view chrome re-reads
 *  what is on screen (it was view-only until now). */
export const ATTRIBUTION_ACCEPTED_EVENT = 'papan:share-attribution-accepted'

async function recordAcceptance(sessionId: string): Promise<boolean> {
  const res = await sessionFetch(`${getApiBase()}/api/share-links/session/${encodeURIComponent(sessionId)}/attribution-disclosure`, {
    method: 'POST', credentials: 'include',
  }).catch(() => null)
  if (res?.ok) window.dispatchEvent(new Event(ATTRIBUTION_ACCEPTED_EVENT))
  return !!res?.ok
}

/** Responses whose write the visitor DECLINED (chose "Not now") — nothing failed, so the
 *  caller shows nothing (`declinedDisclosure`). */
const declined = new WeakSet<Response>()

/** A share-view WRITE request: asks for the disclosure on the server's 409, retries once. Bound to the share session
 *  current when it STARTS (#1316): an answer after that session ended becomes a `SessionEndedError` — the write runs
 *  on the server, the old session shows nothing. */
export function shareWriteFetch(sessionId: string, input: string, init?: RequestInit): Promise<Response> {
  const start = currentSessionSignal()
  return dropIfSessionEnded(shareWriteFetchInSession(sessionId, input, init, start), start)
}

async function shareWriteFetchInSession(sessionId: string, input: string, init: RequestInit | undefined, start: AbortSignal | undefined): Promise<Response> {
  const res = await sessionFetch(input, init)
  if (res.status !== 409) return res
  const body = await res.clone().json().catch(() => null) as { error?: string; disclosure?: AttributionDisclosureInfo } | null
  if (body?.error !== 'attribution_disclosure_required' || !body.disclosure) return res
  const accepted = await requestAttributionDisclosure(sessionId, body.disclosure, start)
  // #1316 (the retry rule): the session the write started in ended while the visitor was asked — never sent again
  // (it would go out as the NEXT session); the outer wrapper drops this refusal, so nothing shows
  if (start?.aborted) return res
  if (accepted) return sessionFetch(input, init)
  declined.add(res)
  return res
}

/** Did the visitor decline the disclosure for this write? Then it was not attempted — no
 *  error to show. */
export function declinedDisclosure(res: Response): boolean {
  return declined.has(res)
}

function usePending(): Pending | null {
  return useSyncExternalStore(
    (l) => { listeners.add(l); return () => { listeners.delete(l) } },
    () => pending,
    () => null,
  )
}

/** The disclosure dialog — mount ONCE in the share-view chrome. */
export function AttributionDisclosureHost({ themeClass }: { themeClass: string }) {
  const { t } = useTranslation('files')
  const p = usePending()
  const [busy, setBusy] = useState(false)
  const [failed, setFailed] = useState(false)

  useEffect(() => { setBusy(false); setFailed(false) }, [p])
  useEffect(() => {
    if (!p) return
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && !busy) settle(false) }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [p, busy])

  if (!p) return null
  const org = p.disclosure.sourceOrgName || t('shareView.attribution.orgFallback')
  const accept = async () => {
    setBusy(true); setFailed(false)
    if (await recordAcceptance(p.sessionId)) settle(true)
    else { setBusy(false); setFailed(true) }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" role="dialog" aria-modal="true" aria-labelledby="attribution-disclosure-title">
      <div className="absolute inset-0 bg-black/50" onClick={() => { if (!busy) settle(false) }} />
      <div className={`${themeClass} themed-bg-secondary themed-text border themed-border rounded-lg shadow-2xl w-full max-w-md p-6 relative mx-4`}>
        <div className="flex flex-col items-center text-center">
          <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-amber-500/15">
            <ShieldAlert size={24} className="text-amber-400" />
          </div>
          <h2 id="attribution-disclosure-title" className="text-base font-semibold themed-text mb-2">
            {t('shareView.attribution.title')}
          </h2>
          <p className="text-sm themed-text-secondary leading-relaxed mb-2">
            {p.disclosure.recordsOrg
              ? t('shareView.attribution.bodyOrg', { email: p.disclosure.email, org })
              : t('shareView.attribution.bodyEmail', { email: p.disclosure.email, org })}
          </p>
          <p className="text-xs themed-text-muted mb-4">{t('shareView.attribution.once', { org })}</p>
          {failed && <p className="text-xs text-red-400 mb-3" role="alert">{t('shareView.attribution.failed')}</p>}
          <div className="flex gap-2 w-full">
            <button
              type="button"
              disabled={busy}
              onClick={() => settle(false)}
              className="flex-1 px-3 py-2 rounded-md text-sm border themed-border themed-text hover:themed-bg-tertiary disabled:opacity-50"
            >
              {t('shareView.attribution.cancel')}
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={() => { void accept() }}
              className="flex-1 px-3 py-2 rounded-md text-sm bg-blue-600 hover:bg-blue-500 text-white disabled:opacity-50"
            >
              {t('shareView.attribution.accept')}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

/** The strip above a read-only editor: "Start editing" asks; the chrome then reloads the view
 *  writable (`ATTRIBUTION_ACCEPTED_EVENT`). */
export function AttributionEditBanner({ sessionId, disclosure }: { sessionId: string; disclosure: AttributionDisclosureInfo }) {
  const { t } = useTranslation('files')
  const org = disclosure.sourceOrgName || t('shareView.attribution.orgFallback')
  return (
    <div className="flex flex-wrap items-center gap-2 px-4 py-1.5 ws-clear text-xs themed-text-secondary" role="status">
      <ShieldAlert size={14} className="text-amber-400 shrink-0" />
      <span className="min-w-0">{t(disclosure.recordsOrg ? 'shareView.attribution.bannerOrg' : 'shareView.attribution.banner', { org })}</span>
      <button
        type="button"
        onClick={() => {
          void requestAttributionDisclosure(sessionId, disclosure, currentSessionSignal())   // closes if the session ends (#1316)
        }}
        className="px-2 py-0.5 rounded bg-blue-600 hover:bg-blue-500 text-white"
      >
        {t('shareView.attribution.startEditing')}
      </button>
    </div>
  )
}
